/**
 * CURRENCIES HOOK - API Methods & React Query Hooks Summary
 *
 * ===========================================
 * API METHODS (Internal Functions):
 * ===========================================
 * • getAllCurrencies()     - GET /currencies - Fetch all currencies
 * • convertCurrencies()    - POST /currencies/convert - Convert currencies
 *
 * ===========================================
 * REACT QUERY HOOKS (Exported):
 * ===========================================
 * • useCurrencies(enabled?)     - Query: Get all currencies
 * • useConvertCurrencies()      - Mutation: Convert currencies
 *
 * ===========================================
 * KEY FEATURES:
 * ===========================================
 * • No authentication required
 * • Long cache time (1 hour) as currencies data is static
 * • Uses EOR API URL for data fetching
 * • Comprehensive error handling
 * ===========================================
 */

;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQueries,
  useQuery,
} from '@tanstack/react-query';
import type { Currency } from '@workspace/ui/types';
import { useMemo } from 'react';
import fetchAPI from '@/lib/fetch-api';

// ==================== INTERFACES ====================

export type ConversionMarginType = 'buy' | 'sell';

/**
 * All wallet conversions apply a -1.5% margin against the raw FX rate per product
 * direction, so callers get a conservative/"sell" rate by default.
 * Flip to 'buy' only if a specific flow is ever confirmed to need +1.5%.
 */
export const DEFAULT_WALLET_CONVERSION_MARGIN: ConversionMarginType = 'sell';

export interface ConvertCurrenciesRequest {
  amount: number;
  from: string;
  to: string;
  marginType?: ConversionMarginType;
}

interface ConvertedAmount {
  amount: number;
  currency_code: string;
}

export interface ConversionData {
  initial: ConvertedAmount;
  converted: ConvertedAmount;
}

// ==================== API FUNCTIONS ====================

/**
 * Fetches all currencies from the API
 * @throws {Error} If the API request fails
 * @returns {Promise<Currency[]>} The list of currencies
 */
export const getAllCurrencies = async (
  allSystemCurrencies: boolean = false
): Promise<Currency[]> => {
  const apiUrl = process.env.NEXT_PUBLIC_EOR_API_URL;

  if (!apiUrl) {
    throw new Error('NEXT_PUBLIC_EOR_API_URL environment variable is not set');
  }

  const response = await fetchAPI<Currency[]>(
    `${apiUrl}/currencies?${!allSystemCurrencies ? 'app_type=wallet' : ''}`,
    {
      method: 'GET',
      cache: 'force-cache',
      next: {
        revalidate: 3600, // 1 hour
        tags: ['currencies'],
      },
    }
  );

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to fetch currencies');
  }

  if (!response.data || !Array.isArray(response.data)) {
    throw new Error('Invalid response format from currencies API');
  }

  return response.data!;
};

/**
 * Converts currencies using the API.
 *
 * The `marginType` is appended to the URL as a path segment (e.g.
 * `/currencies/convert/sell`) and defaults to `DEFAULT_WALLET_CONVERSION_MARGIN`
 * ('sell'), which applies a -1.5% margin to the converted amount. Pass `'buy'`
 * to apply +1.5%, or `null` to call the base endpoint without a margin (raw rate).
 */
export const convertCurrencies = async (
  request: ConvertCurrenciesRequest
): Promise<ConversionData> => {
  const apiUrl = process.env.NEXT_PUBLIC_EOR_API_URL;

  if (!apiUrl) {
    throw new Error('NEXT_PUBLIC_EOR_API_URL environment variable is not set');
  }

  const { marginType = DEFAULT_WALLET_CONVERSION_MARGIN, ...body } = request;
  const marginSegment = marginType ? `/${marginType}` : '';

  const response = await fetchAPI<ConversionData>(`${apiUrl}/currencies/convert${marginSegment}`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to convert currencies');
  }

  if (!response.data) {
    throw new Error('Invalid response format from currency conversion API');
  }

  return response.data!;
};

export function currencyConversionQueryKey(from: string, to: string) {
  return ['currency-conversion', from, to] as const;
}

/**
 * Fetches multiple conversion rates with React Query caching.
 * Each unique from/to pair is requested at most once (shared across components).
 */
export function useCurrencyConversionRates(
  pairs: Array<{ from: string; to: string }>,
  enabled: boolean = true
): Record<string, number> {
  const uniquePairs = useMemo(() => {
    const seen = new Map<string, { from: string; to: string }>();
    for (const pair of pairs) {
      if (pair.from === pair.to) continue;
      const key = `${pair.from}-${pair.to}`;
      seen.set(key, pair);
    }
    return Array.from(seen.values());
  }, [pairs]);

  const results = useQueries({
    queries: uniquePairs.map(({ from, to }) => ({
      queryKey: currencyConversionQueryKey(from, to),
      queryFn: () => convertCurrencies({ amount: 1, from, to }),
      enabled,
      staleTime: 300_000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    })),
  });

  return useMemo(() => {
    const rates: Record<string, number> = {};
    uniquePairs.forEach(({ from, to }, index) => {
      const amount = results[index]?.data?.converted.amount;
      if (amount) rates[`${from}-${to}`] = amount;
    });
    return rates;
  }, [uniquePairs, results]);
}

// ==================== REACT QUERY HOOKS ====================

/**
 * Hook to fetch all currencies
 * @param {boolean} enabled - Whether the query should be enabled
 * @returns {UseQueryResult} Query result for currencies list
 */
export function useCurrencies(
  enabled: boolean = true,
  allSystemCurrencies: boolean = false
): UseQueryResult<Currency[], Error> {
  return useQuery({
    queryKey: ['currencies', { allSystemCurrencies }],
    queryFn: () => getAllCurrencies(allSystemCurrencies),
    enabled,
    staleTime: 3600000, // 1 hour
    gcTime: 3600000, // 1 hour garbage collection time
    refetchOnWindowFocus: false, // Prevent refetch on window focus
    refetchOnReconnect: false, // Prevent refetch on network reconnect
    refetchInterval: false, // Disable automatic refetching
  });
}

/**
 * Hook to convert currencies
 * @returns {UseMutationResult} Mutation result for currency conversion
 */
export function useConvertCurrencies(): UseMutationResult<
  ConversionData,
  Error,
  ConvertCurrenciesRequest
> {
  return useMutation({
    mutationFn: convertCurrencies,
  });
}
